import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useAdminTheme } from '../../contexts/AdminThemeContext';

const WhatsAppReviewModal = ({ record, onClose, onReject, onApprove, isProcessing }) => {
  const { isDarkMode } = useAdminTheme();
  const [modifications, setModifications] = useState({});
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionForm, setShowRejectionForm] = useState(false);

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    onReject(record._id, rejectionReason);
  };

  const handleApprove = () => {
    onApprove(record._id, modifications);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className={`rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
      }`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className={`text-xl font-semibold transition-colors duration-300 ${
              isDarkMode ? 'text-gray-100' : 'text-gray-900'
            }`}>Review WhatsApp Message Processing</h2>
            <button
              onClick={onClose}
              className={`transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Original WhatsApp Message */}
            <div>
              <h3 className={`font-medium mb-3 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>Original WhatsApp Message</h3>
              <div className={`p-4 rounded-lg transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-page-dark/60' : 'bg-gray-50'
              }`}>
                <div className="space-y-2">
                  <p className={`font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    From: {record.rawMessage?.from || 'Unknown'}
                  </p>
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Message ID: <span className="break-all">{record.rawMessage?.messageId}</span>
                  </p>
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Date: {record.rawMessage?.timestamp ? new Date(record.rawMessage.timestamp * 1000).toLocaleString() : 
                           record.rawMessage?.receivedAt ? new Date(record.rawMessage.receivedAt).toLocaleString() : 
                           'Unknown'}
                  </p>
                  <div className="mt-3">
                    <p className={`font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>Message Content:</p>
                    <div className={`p-3 rounded border max-h-96 overflow-y-auto transition-colors duration-300 ${
                      isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
                    }`}>
                      <pre className={`whitespace-pre-wrap text-sm transition-colors duration-300 break-words ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {record.rawMessage?.text || 'No content available'}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Extracted Data */}
            <div>
              <h3 className={`font-medium mb-3 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>AI Extracted Data</h3>
              <div className="space-y-4">
                {/* Customer Information */}
                <div className={`p-4 rounded-lg transition-colors duration-300 ${
                  isDarkMode ? 'bg-skyone-page-dark/60' : 'bg-gray-50'
                }`}>
                  <h4 className={`font-medium mb-3 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>Customer Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Customer Name</label>
                      <input
                        type="text"
                        value={modifications.customer?.name?.value !== undefined ? modifications.customer.name.value : (record.extractedData?.customer?.name?.value || '')}
                        onChange={(e) => setModifications(prev => ({
                          ...prev,
                          customer: {
                            ...prev.customer,
                            name: { value: e.target.value, confidence: 1.0 }
                          }
                        }))}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                      {record.extractedData?.customer?.name?.confidence && (
                        <span className={`text-xs ${getConfidenceColor(record.extractedData.customer.name.confidence)}`}>
                          Confidence: {(record.extractedData.customer.name.confidence * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>

                    {/* Customer Email */}
                    <div>
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Customer Email</label>
                      <input
                        type="email"
                        value={modifications.customer?.email?.value !== undefined ? modifications.customer.email.value : (record.extractedData?.customer?.email?.value || '')}
                        onChange={(e) => setModifications(prev => ({
                          ...prev,
                          customer: {
                            ...prev.customer,
                            email: { value: e.target.value, confidence: 1.0 }
                          }
                        }))}
                        placeholder="Leave blank to auto-generate email from phone number"
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                      {record.extractedData?.customer?.email?.confidence && (
                        <span className={`text-xs ${getConfidenceColor(record.extractedData.customer.email.confidence)}`}>
                          Confidence: {(record.extractedData.customer.email.confidence * 100).toFixed(1)}%
                        </span>
                      )}
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        If left blank, will auto-generate: whatsapp-{record.rawMessage?.from || 'phone'}@airport-service.com
                      </p>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Phone Number</label>
                      <input
                        type="text"
                        value={modifications.customer?.phone?.value !== undefined ? modifications.customer.phone.value : (record.extractedData?.customer?.phone?.value || '')}
                        onChange={(e) => setModifications(prev => ({
                          ...prev,
                          customer: {
                            ...prev.customer,
                            phone: { value: e.target.value, confidence: 1.0 }
                          }
                        }))}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                      {record.extractedData?.customer?.phone?.confidence && (
                        <span className={`text-xs ${getConfidenceColor(record.extractedData.customer.phone.confidence)}`}>
                          Confidence: {(record.extractedData.customer.phone.confidence * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Service Requirements */}
                <div className={`p-4 rounded-lg transition-colors duration-300 ${
                  isDarkMode ? 'bg-skyone-page-dark/60' : 'bg-gray-50'
                }`}>
                  <h4 className={`font-medium mb-3 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>Service Requirements</h4>
                  <div className="space-y-3">
                    <div>
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Service Type</label>
                      <select
                        value={modifications.serviceRequirements?.serviceType?.value !== undefined ? modifications.serviceRequirements.serviceType.value : (record.extractedData?.serviceRequirements?.serviceType?.value || '')}
                        onChange={(e) => setModifications(prev => ({
                          ...prev,
                          serviceRequirements: {
                            ...prev.serviceRequirements,
                            serviceType: { value: e.target.value, confidence: 1.0 }
                          }
                        }))}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      >
                        <option value="">Select Service Type</option>
                        <option value="ARRIVAL">Arrival</option>
                        <option value="DEPARTURE">Departure</option>
                        <option value="TRANSIT">Transit</option>
                      </select>
                      {record.extractedData?.serviceRequirements?.serviceType?.confidence && (
                        <span className={`text-xs ${getConfidenceColor(record.extractedData.serviceRequirements.serviceType.confidence)}`}>
                          Confidence: {(record.extractedData.serviceRequirements.serviceType.confidence * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Service Level</label>
                      <select
                        value={modifications.serviceRequirements?.categoryLevel?.value !== undefined ? modifications.serviceRequirements.categoryLevel.value : (record.extractedData?.serviceRequirements?.categoryLevel?.value || '')}
                        onChange={(e) => setModifications(prev => ({
                          ...prev,
                          serviceRequirements: {
                            ...prev.serviceRequirements,
                            categoryLevel: { value: e.target.value, confidence: 1.0 }
                          }
                        }))}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      >
                        <option value="">Select Service Level</option>
                        <option value="Bronze">Bronze</option>
                        <option value="Silver">Silver</option>
                        <option value="Gold">Gold</option>
                        <option value="VIP">VIP</option>
                      </select>
                      {record.extractedData?.serviceRequirements?.categoryLevel?.confidence && (
                        <span className={`text-xs ${getConfidenceColor(record.extractedData.serviceRequirements.categoryLevel.confidence)}`}>
                          Confidence: {(record.extractedData.serviceRequirements.categoryLevel.confidence * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Number of Passengers</label>
                      <input
                        type="number"
                        min="1"
                        value={modifications.passengers?.totalCount?.value !== undefined ? modifications.passengers.totalCount.value : (record.extractedData?.passengers?.totalCount?.value || '')}
                        onChange={(e) => setModifications(prev => ({
                          ...prev,
                          passengers: {
                            ...prev.passengers,
                            totalCount: { value: parseInt(e.target.value) || 1, confidence: 1.0 }
                          }
                        }))}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                      {record.extractedData?.passengers?.totalCount?.confidence && (
                        <span className={`text-xs ${getConfidenceColor(record.extractedData.passengers.totalCount.confidence)}`}>
                          Confidence: {(record.extractedData.passengers.totalCount.confidence * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Number of Bags</label>
                      <input
                        type="number"
                        min="0"
                        value={modifications.serviceRequirements?.additionalServices?.bags?.count !== undefined ? modifications.serviceRequirements.additionalServices.bags.count : (record.extractedData?.serviceRequirements?.additionalServices?.bags?.count || '')}
                        onChange={(e) => setModifications(prev => ({
                          ...prev,
                          serviceRequirements: {
                            ...prev.serviceRequirements,
                            additionalServices: {
                              ...prev.serviceRequirements?.additionalServices,
                              bags: { 
                                count: parseInt(e.target.value) || 0, 
                                confidence: 1.0,
                                mentioned: true
                              }
                            }
                          }
                        }))}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                      {record.extractedData?.serviceRequirements?.additionalServices?.bags?.confidence && (
                        <span className={`text-xs ${getConfidenceColor(record.extractedData.serviceRequirements.additionalServices.bags.confidence)}`}>
                          Confidence: {(record.extractedData.serviceRequirements.additionalServices.bags.confidence * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Flight Details */}
                <div className={`p-4 rounded-lg transition-colors duration-300 ${
                  isDarkMode ? 'bg-skyone-page-dark/60' : 'bg-gray-50'
                }`}>
                  <h4 className={`font-medium mb-3 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>Flight Details</h4>
                  <div className="space-y-3">
                    <div>
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Service Date</label>
                      <input
                        type="date"
                        value={modifications.serviceRequirements?.serviceDate?.value !== undefined ? modifications.serviceRequirements.serviceDate.value : (record.extractedData?.serviceRequirements?.serviceDate?.value || '')}
                        onChange={(e) => setModifications(prev => ({
                          ...prev,
                          serviceRequirements: {
                            ...prev.serviceRequirements,
                            serviceDate: { value: e.target.value, confidence: 1.0 }
                          }
                        }))}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                      {record.extractedData?.serviceRequirements?.serviceDate?.confidence && (
                        <span className={`text-xs ${getConfidenceColor(record.extractedData.serviceRequirements.serviceDate.confidence)}`}>
                          Confidence: {(record.extractedData.serviceRequirements.serviceDate.confidence * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Service Time</label>
                      <input
                        type="time"
                        value={modifications.serviceRequirements?.serviceTime?.value !== undefined ? modifications.serviceRequirements.serviceTime.value : (record.extractedData?.serviceRequirements?.serviceTime?.value || '')}
                        onChange={(e) => setModifications(prev => ({
                          ...prev,
                          serviceRequirements: {
                            ...prev.serviceRequirements,
                            serviceTime: { value: e.target.value, confidence: 1.0 }
                          }
                        }))}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                      {record.extractedData?.serviceRequirements?.serviceTime?.confidence && (
                        <span className={`text-xs ${getConfidenceColor(record.extractedData.serviceRequirements.serviceTime.confidence)}`}>
                          Confidence: {(record.extractedData.serviceRequirements.serviceTime.confidence * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Flight Number</label>
                      <input
                        type="text"
                        value={modifications.flightDetails?.flightNumber?.value !== undefined ? modifications.flightDetails.flightNumber.value : (record.extractedData?.flightDetails?.flightNumber?.value || '')}
                        onChange={(e) => setModifications(prev => ({
                          ...prev,
                          flightDetails: {
                            ...prev.flightDetails,
                            flightNumber: { value: e.target.value, confidence: 1.0 }
                          }
                        }))}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                      {record.extractedData?.flightDetails?.flightNumber?.confidence && (
                        <span className={`text-xs ${getConfidenceColor(record.extractedData.flightDetails.flightNumber.confidence)}`}>
                          Confidence: {(record.extractedData.flightDetails.flightNumber.confidence * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Departure Airport</label>
                      <input
                        type="text"
                        value={modifications.flightDetails?.from?.value !== undefined ? modifications.flightDetails.from.value : (record.extractedData?.flightDetails?.from?.value || '')}
                        onChange={(e) => setModifications(prev => ({
                          ...prev,
                          flightDetails: {
                            ...prev.flightDetails,
                            from: { value: e.target.value, confidence: 1.0 }
                          }
                        }))}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                      {record.extractedData?.flightDetails?.from?.confidence && (
                        <span className={`text-xs ${getConfidenceColor(record.extractedData.flightDetails.from.confidence)}`}>
                          Confidence: {(record.extractedData.flightDetails.from.confidence * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>Arrival Airport</label>
                      <input
                        type="text"
                        value={modifications.flightDetails?.to?.value !== undefined ? modifications.flightDetails.to.value : (record.extractedData?.flightDetails?.to?.value || '')}
                        onChange={(e) => setModifications(prev => ({
                          ...prev,
                          flightDetails: {
                            ...prev.flightDetails,
                            to: { value: e.target.value, confidence: 1.0 }
                          }
                        }))}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                          isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                      />
                      {record.extractedData?.flightDetails?.to?.confidence && (
                        <span className={`text-xs ${getConfidenceColor(record.extractedData.flightDetails.to.confidence)}`}>
                          Confidence: {(record.extractedData.flightDetails.to.confidence * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 border rounded-md transition-colors duration-300 ${
                isDarkMode ? 'border-skyone-border-dark text-zinc-300 hover:bg-skyone-inset-dark' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={() => setShowRejectionForm(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Reject
            </button>
            <button
              onClick={handleApprove}
              disabled={isProcessing}
              className={`px-4 py-2 rounded-md transition-colors flex items-center space-x-2 ${
                isProcessing 
                  ? (isDarkMode ? 'bg-green-500 text-white cursor-not-allowed' : 'bg-green-400 text-white cursor-not-allowed')
                  : (isDarkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-600 text-white hover:bg-green-700')
              }`}
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Approve & Convert</span>
                </>
              )}
            </button>
          </div>

          {/* Rejection Reason Input */}
          {showRejectionForm && (
            <div className="mt-4 p-4 border rounded-lg">
              <label className={`block text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Rejection Reason</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm transition-colors duration-300 ${
                  isDarkMode ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-100' : 'border-gray-300 bg-white text-gray-900'
                }`}
                rows="3"
              />
              <div className="mt-3 flex justify-end space-x-2">
                <button
                  onClick={() => setShowRejectionForm(false)}
                  className={`px-3 py-1 text-sm border rounded transition-colors duration-300 ${
                    isDarkMode ? 'border-skyone-border-dark text-zinc-300 hover:bg-skyone-inset-dark' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Confirm Rejection
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppReviewModal;
